import { arrayMove } from '@dnd-kit/sortable'
import { useCallback, useState } from 'react'
import { ConditionType, type MappingRule } from '../types'

export default function useComputedFields() {
  const [computedFields, setComputedFields] = useState<MappingRule[]>([])
  const [movedComputedFieldId, setMovedComputedFieldId] = useState<
    string | null
  >(null)
  const [lastAddedComputedFieldId, setLastAddedComputedFieldId] = useState<
    string | null
  >(null)

  const addComputedField = useCallback(() => {
    const id = `computed-${Date.now()}`
    const newRule: MappingRule = {
      id,
      target: '',
      condition: ConditionType.DIRECT_MAPPING,
      source: '',
      normalize: [],
    }
    setComputedFields((prev) => [...prev, newRule])
    setLastAddedComputedFieldId(id)
  }, [])

  const updateComputedField = useCallback((updated: MappingRule) => {
    setComputedFields((prev) =>
      prev.map((r) => (r.id === updated.id ? updated : r))
    )
  }, [])

  const removeComputedField = useCallback((id: string) => {
    setComputedFields((prev) => prev.filter((r) => r.id !== id))
  }, [])

  const triggerMoveAnimation = useCallback((id: string) => {
    setMovedComputedFieldId(id)
    setTimeout(() => setMovedComputedFieldId(null), 400)
  }, [])

  const reorderComputedFields = useCallback(
    (activeId: string, overId: string) => {
      setComputedFields((prev) => {
        if (activeId === overId) return prev
        const oldIndex = prev.findIndex((r) => r.id === activeId)
        const newIndex = prev.findIndex((r) => r.id === overId)
        if (oldIndex < 0 || newIndex < 0) return prev
        if (oldIndex === newIndex) return prev
        return arrayMove(prev, oldIndex, newIndex)
      })
      triggerMoveAnimation(activeId)
    },
    [triggerMoveAnimation]
  )

  const resetComputedFields = useCallback(() => {
    setComputedFields([])
  }, [])

  return {
    computedFields,
    setComputedFields,
    movedComputedFieldId,
    lastAddedComputedFieldId,
    addComputedField,
    updateComputedField,
    removeComputedField,
    reorderComputedFields,
    resetComputedFields,
  }
}
